/**
 * ParUnitControl — a PAR with the unit it was typed in, and the vendor-neutral
 * quantity it actually means.
 *
 * The control this replaces was a 34-pixel box with the unit printed behind
 * the number as a faint watermark, and no way to change that unit. So a PAR
 * read as bare "10" — ten of what? — and an item bought in cases could only be
 * given a PAR in its count unit, which is not how anybody thinks about a
 * shelf. You think "keep two tubs of mayonnaise on hand", not "keep 1,280
 * fluid ounces".
 *
 * Here you can say "2 tubs", and 1,280 fl oz is what gets stored. That
 * distinction is the whole point:
 *
 *   The DISPLAY value is what you typed, kept so the PAR reads back the way
 *   you set it.
 *
 *   The NORMALIZED value is what on-hand is compared against, in the item's
 *   own count unit, and it never moves when the preferred vendor changes. A
 *   PAR of "2 Costco tubs" is ten gallons forever, not "two of whoever is
 *   cheapest this week", which is what re-deriving it on read would mean.
 *
 * What it refuses to do is accept a container word with no product behind it.
 * "2 cases" looks like a quantity and is not one — a Costco case and a Sysco
 * case hold different amounts — so the unit list contains only the item's own
 * unit and vendor packages whose size is confirmed. There is nothing
 * ambiguous to choose.
 *
 * The conversion is a multiply against the server-built ladder, the same rungs
 * the count sheet uses, so the PAR and the count can never disagree about how
 * big a case is.
 *
 * Props:
 *   value            - the display quantity (string or number)
 *   unit             - the display unit
 *   optionId         - purchase option the display unit refers to (null = count unit)
 *   countUnit        - the item's canonical count unit
 *   rungs            - server-built ladder for this item
 *   onCommit         - ({ value, unit, optionId, normalized }) => void, on blur/Enter
 *   disabled?
 *   label?           - defaults to 'PAR'
 *   compact?         - template-card sizing
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { LADDER_LEVELS, MISSING, finiteNonNegative, formatQuantity } from '../utils/countUnitModel.js';

const COUNT_UNIT_CHOICE = '__count_unit__';

/**
 * The units a PAR may be expressed in: the item's own, plus every vendor
 * package whose size is confirmed.
 *
 * Inner-pack rungs are deliberately excluded. "Keep 8 bottles on hand" is a
 * legitimate thought, but a PAR set in inner packs and an order placed in
 * cases invites a rounding argument on every line, and the count unit already
 * expresses it exactly.
 */
export function parUnitChoices(countUnit, rungs) {
  const choices = [{ key: COUNT_UNIT_CHOICE, label: countUnit || 'unit', optionId: null, perUnit: 1 }];
  (rungs || [])
    .filter((rung) => rung.level === LADDER_LEVELS.PACKAGE && rung.qtyInCountUnit > 0)
    .forEach((rung) => {
      choices.push({
        key: rung.key,
        label: rung.label,
        shortLabel: rung.unitLabel,
        optionId: rung.optionId,
        perUnit: rung.qtyInCountUnit,
        detail: rung.detail || null,
      });
    });
  return choices;
}

/**
 * The stored quantity for a typed PAR.
 *
 * Null — never 0 — when the value is missing or the chosen unit has no
 * confirmed size. A PAR of zero is legitimate ("do not stock this"), so it has
 * to stay distinguishable from a PAR we could not work out.
 */
export function normalizedPar(value, choice) {
  const qty = finiteNonNegative(value);
  if (qty === null || !choice) return null;
  const per = Number(choice.perUnit);
  if (!Number.isFinite(per) || per <= 0) return null;
  return Math.round(qty * per * 1e6) / 1e6;
}

export default function ParUnitControl({
  value,
  unit,
  optionId = null,
  countUnit,
  rungs = [],
  onCommit,
  disabled = false,
  label = 'PAR',
  compact = false,
}) {
  const choices = useMemo(() => parUnitChoices(countUnit, rungs), [countUnit, rungs]);

  const matchChoice = () => {
    if (optionId != null) {
      const byOption = choices.find((c) => c.optionId != null && String(c.optionId) === String(optionId));
      if (byOption) return byOption;
    }
    return choices[0];
  };

  const [draft, setDraft] = useState(value == null ? '' : String(value));
  const [choiceKey, setChoiceKey] = useState(matchChoice().key);
  const editingRef = useRef(false);
  const lastCommitted = useRef({ value: value == null ? '' : String(value), key: matchChoice().key });

  // Sync from props only while not being edited, so a background refresh
  // cannot wipe what is being typed. Same rule the template card already uses
  // for its other cells.
  useEffect(() => {
    if (editingRef.current) return;
    const next = value == null ? '' : String(value);
    lastCommitted.current = { ...lastCommitted.current, value: next };
    setDraft(next);
  }, [value]);

  useEffect(() => {
    if (editingRef.current) return;
    setChoiceKey(matchChoice().key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionId, unit, choices.length]);

  const choice = choices.find((c) => c.key === choiceKey) || choices[0];
  const normalized = normalizedPar(draft, choice);
  const inCountUnit = choice.optionId == null;

  const commit = (nextDraft, nextChoice) => {
    const valueText = nextDraft == null ? '' : String(nextDraft);
    if (valueText === lastCommitted.current.value && nextChoice.key === lastCommitted.current.key) return;
    lastCommitted.current = { value: valueText, key: nextChoice.key };
    onCommit?.({
      value: valueText,
      unit: nextChoice.optionId == null ? countUnit : nextChoice.shortLabel || nextChoice.label,
      optionId: nextChoice.optionId,
      normalized: normalizedPar(valueText, nextChoice),
    });
  };

  const size = compact
    ? { font: '0.72rem', height: 24, width: 44, unitFont: '0.66rem' }
    : { font: '0.85rem', height: 30, width: 58, unitFont: '0.75rem' };

  return (
    // flexWrap so the "= 1280 floz" note drops to its own line in a narrow
    // column instead of pushing the picker out of the card.
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '2px 4px', minWidth: 0, maxWidth: '100%' }}>
      <span
        className="fw-bold"
        style={{
          fontSize: compact ? '0.68rem' : '0.75rem',
          color: 'var(--bs-emphasis-color)',
          flexShrink: 0,
        }}
      >
        {label}
      </span>

      <input
        type="text"
        inputMode="decimal"
        aria-label={`${label} quantity`}
        value={draft}
        disabled={disabled}
        onFocus={() => { editingRef.current = true; }}
        onChange={(e) => {
          if (disabled) return;
          editingRef.current = true;
          const next = e.target.value.replace(/[^0-9.]/g, '');
          setDraft(next);
        }}
        onBlur={() => { editingRef.current = false; commit(draft, choice); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
        style={{
          width: size.width,
          flex: `0 0 ${size.width}px`,
          height: size.height,
          fontSize: size.font,
          fontWeight: 600,
          textAlign: 'center',
          padding: '0 3px',
          borderRadius: 4,
          border: disabled ? '1px solid #dee2e6' : '1px solid #f0ad4e',
          background: disabled ? 'rgba(248,249,250,0.7)' : 'rgba(255,243,205,0.7)',
          color: 'var(--bs-emphasis-color)',
          opacity: disabled ? 0.6 : 1,
        }}
      />

      {/* A real select, not a watermark. The unit is the half of a PAR that
          decides what the number means; it cannot be decoration. */}
      <select
        aria-label={`${label} unit`}
        value={choice.key}
        disabled={disabled || choices.length < 2}
        onChange={(e) => {
          const next = choices.find((c) => c.key === e.target.value) || choices[0];
          editingRef.current = false;
          setChoiceKey(next.key);
          commit(draft, next);
        }}
        title={choice.detail || choice.label}
        style={{
          height: size.height,
          fontSize: size.unitFont,
          padding: '0 2px 0 4px',
          borderRadius: 4,
          border: '1px solid #dee2e6',
          background: '#fff',
          color: 'var(--bs-emphasis-color)',
          maxWidth: compact ? 78 : 116,
          minWidth: 0,
          opacity: disabled ? 0.6 : 1,
          cursor: disabled || choices.length < 2 ? 'default' : 'pointer',
        }}
      >
        {choices.map((c) => (
          <option key={c.key} value={c.key}>
            {c.shortLabel || c.label}
          </option>
        ))}
      </select>

      {/* What will actually be stored, shown only when it differs from what was
          typed — otherwise it is noise repeating the field beside it. */}
      {!inCountUnit && (
        <span
          style={{ fontSize: compact ? '0.6rem' : '0.68rem', color: '#8B7B73', whiteSpace: 'nowrap' }}
          title={`Compared against on-hand as ${formatQuantity(normalized, countUnit)}`}
        >
          = {normalized == null ? MISSING : formatQuantity(normalized, countUnit)}
        </span>
      )}
    </div>
  );
}
